import React, { useContext, useEffect, useRef, useCallback, useState } from 'react';
import styles from './styles.module.css';
// import Loader from 'components/utils/Loading';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import { OnlinePurchaseContext } from 'Context/OnlinePurchaseContext';
import PaymentService from 'services/PaymentService';
import { AlertMessagesContext } from 'react-alert-messages';
import { ROUTES, STORAGE_KEYS } from 'const';

function PaymentProcessing() {
  const history = useHistory();
  const { purchaseData, setPurchaseData } = useContext(OnlinePurchaseContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const isMounted = useRef(true);
  const rzpInstance = useRef(null);
  const [isFailed, setIsFailed] = useState(false);

  const processFormBasedPayment = (formData) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = formData.url;

    for (const [key, value] of Object.entries(formData.secrets)) {
      const encRequestInput = document.createElement('input');
      encRequestInput.type = 'hidden';
      encRequestInput.name = key;
      encRequestInput.value = value;
      form.appendChild(encRequestInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const handleBackNavigation = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  }, [history]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = useCallback(
    async (data) => {
      const res = await loadRazorpayScript();

      if (!res) {
        postAlertMessage({ text: 'Razorpay SDK failed to load. Are you online?', type: 'failed' });
        return;
      }

      const redirectToStatusPage = () => {
        if (!isMounted.current) return;
        const payment_id = data.payment_id;
        const secret = data.secret;
        const action = purchaseData?.type;
        history.push(`${ROUTES.PAYMENT_STATUS}?payment_id=${payment_id}&secret=${secret}&action=${action}`);
      };

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name,
        description: data.description,
        order_id: data.tx_id,
        handler: function (response) {
          redirectToStatusPage();
        },
        modal: {
          ondismiss: function () {
            redirectToStatusPage();
          },
        },
      };

      if (!isMounted.current) return;
      const rzp1 = new window.Razorpay(options);
      rzpInstance.current = rzp1;

      rzp1.on('payment.failed', function (response) {
        redirectToStatusPage();
      });

      rzp1.open();
    },
    [purchaseData, postAlertMessage, history]
  );

  const handleBuyButton = useCallback(
    async (isRetry = false) => {
      try {
        setIsFailed(false);
        const data = isRetry ? { ...purchaseData, retry: true } : purchaseData;
        if (data.amount <= 0) {
          handleBackNavigation();
          return;
        }

        const resp = await PaymentService.initiatePayment(data);
        if (!isMounted.current) return;

        if (resp && resp?.payment_url) {
          window.location.href = resp?.payment_url;
          return;
        }
        if (resp && resp?.form_data) {
          processFormBasedPayment(resp?.form_data);
          return;
        }
        if (resp && resp?.key_id && resp?.pg === 'razorpay') {
          handleRazorpayPayment(resp);
          return;
        }
        postAlertMessage({ text: 'Failed to initiate payment', type: 'failed' });
        setIsFailed(true);
      } catch (error) {
        // Update UI and let the user retry the payment again
        setIsFailed(true);
        postAlertMessage({ text: error.message, type: 'failed' });
      }
    },
    [purchaseData, postAlertMessage, handleBackNavigation, handleRazorpayPayment]
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isRetryUrl = queryParams.get('retry') === 'true';

  const isInitialCallDone = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    if (purchaseData && Object.keys(purchaseData).length > 0 && !isInitialCallDone.current) {
      localStorage.setItem(STORAGE_KEYS.PURCHASE_DATA, JSON.stringify(purchaseData));
      handleBuyButton(isRetryUrl);
      isInitialCallDone.current = true;
    } else {
      const storedData = localStorage.getItem(STORAGE_KEYS.PURCHASE_DATA);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setPurchaseData(parsedData);
      }
    }

    return () => {
      isMounted.current = false;
      if (rzpInstance.current) {
        rzpInstance.current.close();
      }
    };
    //eslint-disable-next-line
  }, [isRetryUrl]);

  // Effect to trigger handleBuyButton when purchaseData is loaded from localStorage
  useEffect(() => {
    if (purchaseData && Object.keys(purchaseData).length > 0 && !isInitialCallDone.current && !isFailed) {
      handleBuyButton(isRetryUrl);
      isInitialCallDone.current = true;
    }
  }, [purchaseData, isFailed, handleBuyButton, isRetryUrl]);

  return (
    <Grid container className={styles.container}>
      <Grid
        item
        xs={12}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        rowGap={2}
      >
        {isFailed ? (
          <>
            <h3>Payment Failed!</h3>
            <Button variant="contained" onClick={() => handleBuyButton(true)}>
              Retry
            </Button>
          </>
        ) : (
          <h3>Processing Payment...</h3>
        )}
      </Grid>
    </Grid>
  );
}

export default PaymentProcessing;
